// GM Elements admin panel (§3 Fase 1).
// All enemy/item/skill/world-boss element selectors read from this
// centralized DB collection. Adding a new element here immediately
// makes it selectable across the game engine.

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save, Trash2, X, Flame } from "lucide-react";
import { useElements, invalidateElementsCache } from "@/gm/useElements";

export default function ElementsSection() {
  const cached = useElements();
  const [list, setList] = useState(cached);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => { setList(cached); }, [cached]);
  const refresh = async () => {
    const r = await api.gmElements();
    invalidateElementsCache(r.elements);
    setList(r.elements);
  };

  const filtered = list.filter(e => !q || String(e.name || "").toLowerCase().includes(q.toLowerCase()));

  const save = async (el) => {
    try {
      await api.gmElementUpsert(el);
      await refresh();
      setEditing(null);
      toast.success(el.id ? "Elemento aggiornato" : "Elemento creato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    }
  };
  const del = async (id, key) => {
    if (!window.confirm(`Elimina l'elemento "${key}"? Se in uso verrà rifiutato.`)) return;
    try {
      await api.gmElementDelete(id);
      await refresh();
      toast.success("Eliminato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile eliminare");
    }
  };

  return (
    <div className="space-y-3" data-testid="gm-elements-section">
      <div className="flex items-center gap-2 mb-2">
        <div>
          <div className="font-heading text-sm tracking-[0.3em] uppercase text-[#E5C34A]">Elementi</div>
          <div className="font-mono text-[9px] tracking-widest uppercase text-[#666]">
            {list.length} nel sistema · fonte unica per nemici, oggetti, abilità
          </div>
        </div>
        <button data-testid="gm-element-new" onClick={() => setEditing(defaultElement())}
          className="ml-auto px-3 py-2 border border-[#B89C3A] text-[#E5C34A] hover:bg-[#B89C3A]/10 font-heading text-[10px] tracking-widest uppercase flex items-center gap-1.5">
          <Plus className="w-3 h-3" /> Nuovo elemento
        </button>
      </div>

      <input data-testid="gm-elements-search" value={q} onChange={e => setQ(e.target.value)}
        placeholder="Cerca..."
        className="w-full bg-[#0a0a0a] border border-[#333] px-2.5 py-2 text-xs text-[#E2DFD8] focus:border-[#B89C3A] outline-none" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(el => (
          <div key={el.id} data-testid={`gm-element-card-${el.key}`}
            className="panel p-3 flex gap-3 items-start hover:border-[#E5C34A] transition-colors">
            <div className="w-12 h-12 rounded-sm flex items-center justify-center border border-[#333]"
              style={{ backgroundColor: (el.color || "#333") + "22", borderColor: el.color || "#333" }}>
              <Flame className="w-6 h-6" style={{ color: el.color || "#E5C34A" }} strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-[13px] uppercase tracking-widest truncate" style={{ color: el.color || "#E5C34A" }}>
                {el.name}
              </div>
              <div className="font-mono text-[9px] tracking-widest uppercase text-[#666]">
                key: {el.key} {el.active === false && "· disattivato"}
              </div>
              <div className="text-[11px] text-[#B0ADA5] mt-1 line-clamp-2">{el.description}</div>
              {(el.strong_vs?.length || el.weak_vs?.length) && (
                <div className="mt-1 font-mono text-[9px]">
                  {el.strong_vs?.length > 0 && <div className="text-[#6E8B3D]">forte vs: {el.strong_vs.join(", ")}</div>}
                  {el.weak_vs?.length > 0 && <div className="text-[#E33333]">debole vs: {el.weak_vs.join(", ")}</div>}
                </div>
              )}
              <div className="flex gap-2 mt-2">
                <button onClick={() => setEditing(el)} className="font-mono text-[9px] uppercase tracking-widest text-[#B89C3A]">Modifica</button>
                <button onClick={() => del(el.id, el.key)} className="font-mono text-[9px] uppercase tracking-widest text-[#E33333] ml-auto">Elimina</button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center text-xs italic text-[#666] py-8">Nessun elemento.</div>}
      </div>

      {editing && <ElementEditor initial={editing} available={list} onCancel={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function defaultElement() {
  return { key: "", name: "", color: "#B0ADA5", description: "", strong_vs: [], weak_vs: [], icon: "", active: true, order: 0 };
}

function ElementEditor({ initial, available, onCancel, onSave }) {
  const [el, set] = useState({ ...defaultElement(), ...initial });
  const u = (k, v) => set(p => ({ ...p, [k]: v }));
  const toggleIn = (field, key) => set(p => {
    const arr = new Set(p[field] || []);
    if (arr.has(key)) arr.delete(key); else arr.add(key);
    return { ...p, [field]: Array.from(arr) };
  });
  const otherKeys = available.filter(x => x.key !== el.key).map(x => x.key);

  return (
    <div className="fixed inset-0 z-40 bg-black/80 flex items-center justify-center px-3 sm:px-6 py-4 overflow-y-auto" data-testid="gm-element-editor">
      <div className="panel w-full max-w-2xl space-y-3 my-auto">
        <div className="flex items-center gap-2 border-b border-[#262626] px-4 py-3 sticky top-0 bg-[#050505]">
          <span className="font-heading text-sm tracking-[0.3em] uppercase text-[#E5C34A]">{initial.id ? "Modifica elemento" : "Nuovo elemento"}</span>
          <button onClick={onCancel} className="ml-auto text-[#666] hover:text-[#E2DFD8]"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-4 pb-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Field label="KEY (unico, no spazi)" value={el.key}
              onChange={v => u("key", String(v).replace(/\s+/g, ""))}
              testid="element-key" disabled={!!initial.id} />
            <Field label="Nome" value={el.name} onChange={v => u("name", v)} testid="element-name" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Field label="Colore (hex)" value={el.color} onChange={v => u("color", v)} testid="element-color" />
            <Field label="Ordine" type="number" value={el.order || 0} onChange={v => u("order", Number(v))} testid="element-order" />
          </div>
          <Field label="Descrizione" textarea value={el.description} onChange={v => u("description", v)} testid="element-desc" />
          <div className="space-y-1">
            <div className="font-mono text-[9px] tracking-widest uppercase text-[#6E8B3D]">FORTE CONTRO</div>
            <div className="flex flex-wrap gap-1">
              {otherKeys.map(k => (
                <button key={k} type="button" onClick={() => toggleIn("strong_vs", k)}
                  className={`px-2 py-1 border font-mono text-[9px] uppercase ${(el.strong_vs || []).includes(k) ? "border-[#6E8B3D] text-[#6E8B3D] bg-[#4A5D23]/10" : "border-[#262626] text-[#666]"}`}>{k}</button>
              ))}
            </div>
          </div>
          <div className="space-y-1">
            <div className="font-mono text-[9px] tracking-widest uppercase text-[#E33333]">DEBOLE CONTRO</div>
            <div className="flex flex-wrap gap-1">
              {otherKeys.map(k => (
                <button key={k} type="button" onClick={() => toggleIn("weak_vs", k)}
                  className={`px-2 py-1 border font-mono text-[9px] uppercase ${(el.weak_vs || []).includes(k) ? "border-[#E33333] text-[#E33333] bg-[#8A0303]/10" : "border-[#262626] text-[#666]"}`}>{k}</button>
              ))}
            </div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={el.active !== false} onChange={e => u("active", e.target.checked)}
              data-testid="element-active" className="accent-[#B89C3A]" />
            <span className="font-mono text-[10px] uppercase">Attivo (selezionabile nel gioco)</span>
          </label>
          <div className="flex justify-end gap-2 pt-2 border-t border-[#262626]">
            <button onClick={onCancel} className="px-3 py-2 border border-[#262626] text-[#8c8c8c] font-heading text-[10px] tracking-widest uppercase">Annulla</button>
            <button data-testid="element-save" onClick={() => {
              if (!el.key || !el.name) { toast.error("Key e nome richiesti"); return; }
              onSave(el);
            }}
              className="px-3 py-2 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase hover:bg-[#4A5D23]/10">
              <Save className="w-3 h-3 inline mr-1" /> Salva
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", textarea, testid, disabled }) {
  const cls = "w-full bg-[#0a0a0a] border border-[#333] px-2.5 py-1.5 text-xs text-[#E2DFD8] focus:border-[#B89C3A] outline-none";
  return (
    <label className="block">
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c] mb-1">{label}</div>
      {textarea ? (
        <textarea data-testid={testid} value={value ?? ""} onChange={e => onChange(e.target.value)} rows={3} className={`${cls} resize-none`} />
      ) : (
        <input data-testid={testid} type={type} value={value ?? ""} disabled={disabled}
          onChange={e => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
          className={cls} />
      )}
    </label>
  );
}
